'use client';

import React, { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import {
  Users, Loader2, AlertCircle, ArrowDownCircle, ArrowUpCircle, Search, X,
} from 'lucide-react';
import { getInsiderTrades, getInsiderTradesBySymbol, type InsiderTrade } from '@/lib/api-client';
import { formatCurrency, cn, decodeHtmlEntities } from '@/lib/utils';
import { useTimezone } from '@/hooks/useTimezone';

// ── Loading skeleton ──────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="space-y-0">
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 px-6 py-4 border-b border-default/50">
          <div className="w-20 h-4 bg-border-default rounded animate-pulse" />
          <div className="w-14 h-4 bg-border-default rounded animate-pulse" />
          <div className="flex-1 h-4 bg-border-default rounded animate-pulse" />
          <div className="w-24 h-4 bg-border-default rounded animate-pulse" />
          <div className="w-14 h-5 bg-border-default rounded-full animate-pulse" />
          <div className="w-16 h-4 bg-border-default rounded animate-pulse" />
          <div className="w-20 h-4 bg-border-default rounded animate-pulse" />
        </div>
      ))}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDisplayDate(dateStr: string, tz?: string): string {
  if (!dateStr) return '--';
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return '--';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: tz });
}

function formatShares(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

type FilterType = 'all' | 'buy' | 'sell';

// ── Main Page ─────────────────────────────────────────────────────────────────

interface TopInsider {
  name: string;
  title: string;
  tradeCount: number;
  totalValue: number;
  lastTradeDate: string;
  symbols: string[];
}

export default function InsiderTradingPage() {
  const tz = useTimezone();
  const [trades, setTrades] = useState<InsiderTrade[]>([]);
  const [topInsiders, setTopInsiders] = useState<TopInsider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [search, setSearch] = useState('');

  // Most-active insiders — loaded once (independent of the ticker search).
  useEffect(() => {
    fetch('/api/insiders/top').then((r) => r.json()).then((r) => setTopInsiders(r.data ?? [])).catch(() => {});
  }, []);

  // Transactions — recent across all tickers, or scoped to a searched ticker.
  // Debounced so typing a symbol doesn't fire a request per keystroke.
  useEffect(() => {
    const q = search.trim().toUpperCase();
    setLoading(true);
    setError('');
    const id = setTimeout(() => {
      (q ? getInsiderTradesBySymbol(q) : getInsiderTrades(50))
        .then((data) => setTrades(Array.isArray(data) ? data : []))
        .catch(() => setError('Failed to load insider trading data.'))
        .finally(() => setLoading(false));
    }, q ? 350 : 0);
    return () => clearTimeout(id);
  }, [search]);

  const filtered = useMemo(() => {
    const base = filter === 'all' ? trades : trades.filter((t) => t.transactionType === filter);
    return [...base].sort((a, b) => (b.transactionDate ?? '').localeCompare(a.transactionDate ?? ''));
  }, [trades, filter]);

  const filterButtons: { label: string; value: FilterType }[] = [
    { label: 'All', value: 'all' },
    { label: 'Buys Only', value: 'buy' },
    { label: 'Sells Only', value: 'sell' },
  ];

  return (
    <div className="min-h-screen bg-surface text-white">
      <div className="px-2 sm:px-3 lg:px-4 py-8">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
                <Users size={18} className="text-violet-400" />
              </div>
              <h1 className="text-2xl font-bold text-white">Insider Trading Activity</h1>
            </div>
            <p className="text-fg-muted text-sm ml-12">
              Recent insider purchases and sales filed with the SEC
            </p>
          </div>

          {/* Ticker search + filter buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-subtle pointer-events-none" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value.toUpperCase())}
              placeholder="Search ticker…"
              className="w-full sm:w-44 pl-8 pr-7 py-1.5 rounded-lg bg-card-bg border border-default text-sm text-white placeholder:text-fg-subtle focus:outline-none focus:border-violet-500/60"
              aria-label="Search insider trades by ticker"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-fg-subtle hover:text-white"
                aria-label="Clear search"
              >
                <X size={13} />
              </button>
            )}
          </div>
          <div className="flex rounded-lg overflow-hidden border border-default">
            {filterButtons.map((fb) => (
              <button
                key={fb.value}
                onClick={() => setFilter(fb.value)}
                className={cn(
                  'px-4 py-1.5 text-xs font-medium transition-colors',
                  filter === fb.value
                    ? 'bg-violet-500 text-white'
                    : 'bg-card-bg text-fg-muted hover:text-slate-200',
                )}
              >
                {fb.label}
              </button>
            ))}
          </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl mb-6 text-red-400">
            <AlertCircle size={18} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Top Insiders Row */}
        {topInsiders.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-fg-muted uppercase tracking-wide mb-3">Most Active Insiders</h2>
            <div className="flex gap-3 overflow-x-auto scrollbar-thin pb-2">
              {topInsiders.slice(0, 12).map((insider) => (
                <Link
                  key={insider.name}
                  href={`/insiders/person/${encodeURIComponent(insider.name)}`}
                  className="flex flex-col gap-1.5 px-4 py-3 bg-card-bg border border-default rounded-xl min-w-[160px] flex-shrink-0 hover:bg-card-hover-bg hover:border-muted transition-all"
                >
                  <p className="text-xs font-semibold text-white leading-tight truncate">{decodeHtmlEntities(insider.name)}</p>
                  {insider.title && <p className="text-[10px] text-fg-subtle truncate">{decodeHtmlEntities(insider.title)}</p>}
                  <div className="flex items-center gap-2 text-[10px] text-fg-muted">
                    <span>{insider.tradeCount} trades</span>
                    <span className="text-emerald-400 font-mono">
                      {insider.totalValue >= 1_000_000
                        ? `$${(insider.totalValue / 1_000_000).toFixed(1)}M`
                        : insider.totalValue >= 1_000
                          ? `$${(insider.totalValue / 1_000).toFixed(0)}K`
                          : `$${insider.totalValue.toLocaleString()}`}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {insider.symbols.slice(0, 3).map((s) => (
                      <span key={s} className="text-[10px] font-mono text-sky-400 bg-sky-500/10 px-1.5 py-0.5 rounded">{s}</span>
                    ))}
                    {insider.symbols.length > 3 && <span className="text-[10px] text-fg-subtle">+{insider.symbols.length - 3}</span>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-card-bg border border-default rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-default">
            <div className="flex items-center gap-2">
              <Users size={16} className="text-violet-400" />
              <span className="text-sm font-medium text-slate-200">
                Insider Transactions
              </span>
              {!loading && (
                <span className="text-xs text-fg-subtle">
                  · {filtered.length} trade{filtered.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

          {loading && <LoadingSkeleton />}

          {!loading && !error && filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
              <div className="w-14 h-14 rounded-full bg-card-bg border border-default flex items-center justify-center">
                <Users size={22} className="text-slate-600" />
              </div>
              <div>
                <p className="text-fg-muted font-medium">No insider trades found</p>
                <p className="text-slate-600 text-sm mt-1">Try changing the filter or check back later.</p>
              </div>
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-default bg-surface/40">
                      <th className="text-left px-6 py-3 text-xs font-semibold text-fg-subtle uppercase tracking-wide">Date</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-fg-subtle uppercase tracking-wide">Symbol</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-fg-subtle uppercase tracking-wide">Insider</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-fg-subtle uppercase tracking-wide">Title</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-fg-subtle uppercase tracking-wide">Type</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-fg-subtle uppercase tracking-wide">Shares</th>
                      <th className="text-right px-6 py-3 text-xs font-semibold text-fg-subtle uppercase tracking-wide">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((t, idx) => (
                      <tr
                        key={t.id}
                        className={cn(
                          'border-b border-default/50 transition-colors hover:bg-card-hover-bg',
                          idx % 2 === 0 ? 'bg-transparent' : 'bg-surface/20',
                        )}
                      >
                        <td className="px-6 py-4 text-slate-300 font-mono text-sm">
                          {formatDisplayDate(t.transactionDate, tz)}
                        </td>
                        <td className="px-4 py-4">
                          <Link
                            href={`/stocks/${t.symbol}`}
                            className="font-bold text-sky-400 hover:text-sky-300 transition-colors"
                          >
                            {t.symbol}
                          </Link>
                        </td>
                        <td className="px-4 py-4 font-medium">
                          <Link href={`/insiders/person/${encodeURIComponent(t.name)}`} className="text-slate-200 hover:text-violet-400 transition-colors">
                            {decodeHtmlEntities(t.name)}
                          </Link>
                        </td>
                        <td className="px-4 py-4 text-fg-muted text-xs">{decodeHtmlEntities(t.title)}</td>
                        <td className="px-4 py-4 text-center">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold',
                              t.transactionType === 'buy'
                                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20'
                                : 'bg-red-500/15 text-red-400 border border-red-500/20',
                            )}
                          >
                            {t.transactionType === 'buy' ? (
                              <ArrowUpCircle size={11} />
                            ) : (
                              <ArrowDownCircle size={11} />
                            )}
                            {t.transactionType}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right text-slate-200 font-mono">
                          {formatShares(t.shares)}
                        </td>
                        <td className="px-6 py-4 text-right font-semibold font-mono">
                          <span className={t.transactionType === 'buy' ? 'text-emerald-400' : 'text-red-400'}>
                            {t.value != null ? formatCurrency(t.value, true) : '—'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-border-default/50">
                {filtered.map((t) => (
                  <div key={t.id} className="px-4 py-4 hover:bg-card-hover-bg transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Link
                          href={`/stocks/${t.symbol}`}
                          className="font-bold text-sky-400 hover:text-sky-300 transition-colors text-sm"
                        >
                          {t.symbol}
                        </Link>
                        <span
                          className={cn(
                            'inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-semibold',
                            t.transactionType === 'buy'
                              ? 'bg-emerald-500/15 text-emerald-400'
                              : 'bg-red-500/15 text-red-400',
                          )}
                        >
                          {t.transactionType}
                        </span>
                      </div>
                      <span className={cn('font-semibold font-mono text-sm', t.transactionType === 'buy' ? 'text-emerald-400' : 'text-red-400')}>
                        {t.value != null ? formatCurrency(t.value, true) : '—'}
                      </span>
                    </div>
                    <p className="text-xs"><Link href={`/insiders/person/${encodeURIComponent(t.name)}`} className="text-slate-300 hover:text-violet-400 transition-colors">{decodeHtmlEntities(t.name)}</Link> <span className="text-fg-subtle">· {decodeHtmlEntities(t.title)}</span></p>
                    <div className="flex items-center gap-3 text-xs text-fg-subtle mt-1">
                      <span>{formatDisplayDate(t.transactionDate, tz)}</span>
                      <span>{formatShares(t.shares)} shares</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="mt-6 text-xs text-slate-600">
          Data sourced from SEC Form 4 filings. Transactions may be delayed.
        </div>
      </div>
    </div>
  );
}
